import {Primitive} from 'utility-types';

import {MessageBoxAction} from './message-box/message-box-actions';
import {MessageBoxState} from './message-box/message-box-reducer';
import {ThunkAction} from 'redux-thunk';
import {AnyAction} from 'redux';
import {SearchPageAction, SearchPageState} from './search/duck/search-types';
import {SettingsState} from './settings/duck/settings-types';
import {SettingsAction} from './settings/duck/settings-actions';
import {ConfigAction} from './config/config-actions';
import {HibuUiConfiguration} from './config/config-types';
import {Translations} from './backendTranslations/backendTranslations-types';
import {TranslationsAction} from './backendTranslations/backendTranslations-actions';
import {FacetFiltersState} from './facets/duck/facet-filter-type';

export interface ReduxNormalizedType<T> {
    byKey: {
        [key: string]: T | undefined;
    };
    allKeys: string[];
}

/**
 * Should it be necessary to access elements by index as well.
 */
export interface ReduxIndexedNormalizedType<T> extends ReduxNormalizedType<T> {
    index: {
        [key: string]: number; // key: id, value: index
    };
    docsLengthIncludingDuplicates: number;
}

export const emptyReduxNormalizedState = <T>(): ReduxNormalizedType<T> => {
    return {byKey: {}, allKeys: []};
};

export const emptyReduxIndexedNormalizedState = <T>(): ReduxIndexedNormalizedType<T> => {
    return {
        ...emptyReduxNormalizedState(),
        index: {},
        docsLengthIncludingDuplicates: 0,
    };
};

export const getNormalizedElementByIndex = <T>(state: ReduxIndexedNormalizedType<T>, index: number): T => {
    if (index >= state.allKeys.length || index < 0) {
        throw new Error(`The index [${index}] is out of bounds for state with length [${state.allKeys.length}]`);
    }
    const id: string = state.allKeys[index];
    const element = state.byKey[id];
    if (element === undefined) {
        throw new Error(`No element exists for id [${id}] with index ${index} in state.`);
    }
    return element;
};

/**
 * Accepts any primitive type, or any primitive type in array form, even nested.
 */
export type PrimitiveOrArray = Primitive | Array<Primitive | Primitive[]>;

export interface State {
    messages: MessageBoxState;
    search: SearchPageState;
    settings: SettingsState;
    config: HibuUiConfiguration;
    translations: Translations;
    facetFilter: FacetFiltersState;
}

export type Action = MessageBoxAction | SearchPageAction | SettingsAction | ConfigAction | TranslationsAction;

/* eslint-disable no-use-before-define */
export type GetState = () => State;
export type PromiseAction = Promise<Action>;
// TODO: remove when migrated to TypeScript fully
export type TempThunkAction = (dispatch: Dispatch, getState: GetState) => any;
export type Dispatch = (
    action: Action | ThunkAction<any, any, any, AnyAction> | TempThunkAction | PromiseAction | Promise<void>
) => any;
/* eslint-enable no-use-before-define */
